import { Presenter } from '../../../common/ddd/presenter.base';
import { LogManager } from '../../../common/port/log/log.manager.port';
import { BaseScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/base-score-goal.entity';
import { BaseAppGoalEntity } from '../../../core/domain/entities/goals/app-goals/base-app-goal.entity';
import { SelfDigitalIntensityScoreGoalMapper } from '../../../infrastructure/mappers/goals/self-digital-intensity-score-goal.mapper';
import { SelfDigitalWorkingHoursScoreGoalMapper } from '../../../infrastructure/mappers/goals/self-digital-working-hours-score-goal.mapper';
import { SelfTimeInCallsScoreGoalMapper } from '../../../infrastructure/mappers/goals/self-time-in-calls-score-goal.mapper';
import { SelfTimeInMeetingsScoreGoalMapper } from '../../../infrastructure/mappers/goals/self-time-in-meetings-score-goal.mapper';
import { SelfScreentimeScoreGoalMapper } from '../../../infrastructure/mappers/goals/self-screentime-score-goal.mapper';
import { SelfFocusScoreGoalMapper } from '../../../infrastructure/mappers/goals/self-focus-score-goal.mapper';
import { SelfTimeInAppGoalMapper } from '../../../infrastructure/mappers/goals/self-time-in-app-goal.mapper';
import { SelfInteractionsGoalMapper } from '../../../infrastructure/mappers/goals/self-interactions-goal.mapper';
import { SelfCallTimeInAppGoalMapper } from '../../../infrastructure/mappers/goals/self-call-time-in-app-goal.mapper';
import { SelfCalendarTimeInAppGoalMapper } from '../../../infrastructure/mappers/goals/self-calendar-time-in-app-goal.mapper';
import { GetSelfGoalSummaryResponseDto } from '../../dtos/goals/get-self-goal-summaries.response.dto';
import { SelfDigitalIntensityScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/self-digital-intensity-score-goal.entity';
import { SelfDigitalWorkingHoursScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/self-digital-working-hours-score-goal.entity';
import { SelfTimeInCallsScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/self-time-in-calls-score-goal.entity';
import { SelfTimeInMeetingsScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/self-time-in-meetings-score-goal.entity';
import { SelfScreentimeScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/self-screentime-score-goal.entity';
import { SelfFocusScoreGoalEntity } from '../../../core/domain/entities/goals/score-goals/self-focus-score-goal.entity';
import { SelfTimeInAppGoalEntity } from '../../../core/domain/entities/goals/app-goals/self-time-in-app-goal.entity';
import { SelfInteractionsGoalEntity } from '../../../core/domain/entities/goals/app-goals/self-interactions-goal.entity';
import { SelfCallTimeInAppGoalEntity } from '../../../core/domain/entities/goals/app-goals/self-call-time-in-app-goal.entity';
import { SelfCalendarTimeInAppGoalEntity } from '../../../core/domain/entities/goals/app-goals/self-calendar-time-in-app-goal.entity';

type GoalBaseEntities = BaseScoreGoalEntity | BaseAppGoalEntity;

export class GetSelfGoalSummaryPresenter
  implements Presenter<GoalBaseEntities, GetSelfGoalSummaryResponseDto>
{
  constructor(
    protected readonly logManager: LogManager,
    protected readonly calendarTimeInAppGoalMapper: SelfCalendarTimeInAppGoalMapper,
    protected readonly callTimeInAppGoalMapper: SelfCallTimeInAppGoalMapper,
    protected readonly digitalIntensityScoreGoalMapper: SelfDigitalIntensityScoreGoalMapper,
    protected readonly digitalWorkingHoursScoreGoalMapper: SelfDigitalWorkingHoursScoreGoalMapper,
    protected readonly focusScoreGoalMapper: SelfFocusScoreGoalMapper,
    protected readonly interactionsGoalMapper: SelfInteractionsGoalMapper,
    protected readonly screentimeScoreGoalMapper: SelfScreentimeScoreGoalMapper,
    protected readonly timeInAppGoalMapper: SelfTimeInAppGoalMapper,
    protected readonly timeInCallsScoreGoalMapper: SelfTimeInCallsScoreGoalMapper,
    protected readonly timeInMeetingsScoreGoalMapper: SelfTimeInMeetingsScoreGoalMapper
  ) {}

  private isBaseScoreGoalEntity(
    entity: GoalBaseEntities
  ): entity is BaseScoreGoalEntity {
    return entity instanceof BaseScoreGoalEntity;
  }

  private isBaseAppGoalEntity(
    entity: GoalBaseEntities
  ): entity is BaseAppGoalEntity {
    return entity instanceof BaseAppGoalEntity;
  }

  toResponse(entity: GoalBaseEntities): GetSelfGoalSummaryResponseDto {
    try {
      if (this.isBaseScoreGoalEntity(entity)) {
        if (entity instanceof SelfDigitalIntensityScoreGoalEntity) {
          return this.digitalIntensityScoreGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfDigitalWorkingHoursScoreGoalEntity) {
          return this.digitalWorkingHoursScoreGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfTimeInCallsScoreGoalEntity) {
          return this.timeInCallsScoreGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfTimeInMeetingsScoreGoalEntity) {
          return this.timeInMeetingsScoreGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfScreentimeScoreGoalEntity) {
          return this.screentimeScoreGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfFocusScoreGoalEntity) {
          return this.focusScoreGoalMapper.toResponse(entity);
        }
      }

      if (this.isBaseAppGoalEntity(entity)) {
        if (entity instanceof SelfTimeInAppGoalEntity) {
          return this.timeInAppGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfInteractionsGoalEntity) {
          return this.interactionsGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfCallTimeInAppGoalEntity) {
          return this.callTimeInAppGoalMapper.toResponse(entity);
        }
        if (entity instanceof SelfCalendarTimeInAppGoalEntity) {
          return this.calendarTimeInAppGoalMapper.toResponse(entity);
        }
      }
    } catch (error) {
      throw new Error(
        `GetSelfGoalSummaryPresenter.toResponse encountered an error ${error}`
      );
    }
  }
}
